import * as React from "react";
import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext.tsx";

// API base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.athenex.xyz";

interface UploadBoxProps {
  title: string;
  onFileSelect: (isSelected: boolean) => void;
  fileType: "protein" | "compound";
  acceptedFileTypes: string;
  fileTypeDisplay: string;
}

const UploadBox: React.FC<UploadBoxProps> = ({ 
  title, 
  onFileSelect, 
  fileType, 
  acceptedFileTypes,
  fileTypeDisplay,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isAuthenticated, getAuthHeaders } = useAuth();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      setError(null);
  
      if (!isAuthenticated) {
        setError("Authentication required");
        return;
      }

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const validFiles = Array.from(files).filter(file => {
          const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
          return acceptedFileTypes.split(',').includes(fileExtension);
        });

        if (validFiles.length === 0) {
          setError(`Only ${acceptedFileTypes} files are accepted`);
          return;
        }

        setIsUploading(true);
        
        try {
          // Upload each file individually
          const uploadPromises = validFiles.map(async (file) => {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("file_type", fileType === "protein" ? "protein_pdb" : "drug_smiles");
            
            const headers = {
              ...getAuthHeaders(),
              "Content-Type": "multipart/form-data"
            };
            
            return axios.post(`${API_BASE_URL}/file_manager/upload`, formData, { headers });
          });

          const responses = await Promise.all(uploadPromises);
          console.log(`Responses from ${fileType} uploads:`, responses.map(r => r.data));
          setIsSelected(true);
          onFileSelect(true);
          setError(null);
        } catch (err: any) {
          console.error(`Error uploading ${fileType} files:`, err);
          setError(err.response?.data?.message || `Failed to upload ${fileType} files`);
        } finally {
          setIsUploading(false);
        }
      }
    },
    [onFileSelect, fileType, acceptedFileTypes, isAuthenticated, getAuthHeaders]
  );

  const handleClick = useCallback(() => {
    setError(null);
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      
      if (!isAuthenticated) {
        setError("Authentication required");
        return;
      }
      
      if (files && files.length > 0) {
        setIsUploading(true);
        
        try {
          // Upload each file individually
          const uploadPromises = Array.from(files).map(async (file) => {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("file_type", fileType === "protein" ? "protein_pdb" : "drug_smiles");
            
            const headers = {
              ...getAuthHeaders(),
              "Content-Type": "multipart/form-data"
            };
            
            return axios.post(`${API_BASE_URL}/file_manager/upload`, formData, { headers });
          });

          const responses = await Promise.all(uploadPromises);
          console.log(`Responses from ${fileType} uploads:`, responses.map(r => r.data));
          setIsSelected(true);
          onFileSelect(true);
          setError(null);
        } catch (err: any) {
          console.error(`Error uploading ${fileType} files:`, err);
          setError(err.response?.data?.message || `Failed to upload ${fileType} files`);
        } finally {
          setIsUploading(false);
        }
      }
    },
    [onFileSelect, fileType, isAuthenticated, getAuthHeaders]
  );

  const handleMouseEnter = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const unselectedColor = "bg-upload-button-base";
  const hoveringColor = "bg-upload-button-hover";
  const selectedColor = "bg-upload-button-pressed";

  const subTextBaseColor = "text-upload-button-subtext-base";
  const subTextHoverColor = "text-upload-button-subtext-hover";
  const subTextSelectedColor = "text-upload-button-subtext-pressed";

  const TextBaseColor = "text-upload-button-text-base";
  const TextHoverColor = "text-upload-button-text-hover";
  const TextSelectedColor = "text-upload-button-text-pressed";

  const boxColor = isSelected ? selectedColor : isDragging ? hoveringColor : unselectedColor;
  const textColor = isSelected ? TextSelectedColor : isDragging ? TextHoverColor : TextBaseColor;
  const subTextColor = isSelected ? subTextSelectedColor : isDragging ? subTextHoverColor : subTextBaseColor;

  return (
    <div
      className={`flex flex-col justify-center h-full rounded-2xl ${boxColor} text-neutral-400 tracking-[2px] w-full cursor-pointer`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
        accept={acceptedFileTypes}
        multiple={true}
      />
      <div className="text-center">
        <span className={`text-5xl font-space-grotesk tracking-[6px] ${textColor}`}>{title}</span>
        <br />
        <br />
        <span className={`text-3xl font-space-grotesk ${subTextColor} tracking-[3.2px]`}>{fileTypeDisplay}</span>
        
        {isUploading && (
          <div className="mt-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="text-gray-600 mt-2">Uploading...</p>
          </div>
        )}
        
        {error && (
          <p className="text-red-500 mt-2 text-sm">{error}</p>
        )}
        
        {!isAuthenticated && !isUploading && (
          <p className="text-amber-500 mt-2">Authentication required</p>
        )}
      </div>
    </div>
  );
};

export const FileUploadSection: React.FC = () => {
  const navigate = useNavigate();
  const [targetSelected, setTargetSelected] = useState(false);
  const [compoundSelected, setCompoundSelected] = useState(false);
  const { isAuthenticated, authError, isLoading } = useAuth();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Navigate to results when both files are selected
  useEffect(() => {
    if (targetSelected && compoundSelected) {
      navigate('/results');
    }
  }, [targetSelected, compoundSelected, navigate]);

  // Show loading state while authentication is being checked
  if (isLoading) {
    return (
      <div className="h-[calc(99vh-theme(spacing.20))] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        <p className="ml-4 text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="h-[calc(99vh-theme(spacing.20))] flex flex-col p-3">
      {authError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 relative">
          <strong className="font-bold">Authentication Error: </strong>
          <span className="block sm:inline">{authError}</span>
          <button 
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => navigate('/')}
          >
            Return to Login
          </button>
        </div>
      )}
      
      <div className="flex gap-2 h-full">
        <div className="w-6/12">
          <UploadBox 
            title="Target(s)" 
            onFileSelect={(isSelected) => setTargetSelected(isSelected)}
            fileType="protein"
            acceptedFileTypes=".pdb"
            fileTypeDisplay="(.pdb)"
          />
        </div>
        <div className="w-6/12">
          <UploadBox 
            title="Drug(s)" 
            onFileSelect={(isSelected) => setCompoundSelected(isSelected)} 
            fileType="compound"
            acceptedFileTypes=".txt"
            fileTypeDisplay="(.txt)"
          />
        </div>
      </div>
    </div>
  );
};